import { useState } from 'react';
import { Search } from 'lucide-react';
import toast from 'react-hot-toast';
import { mattermostService, type MMMessage, type MMTeam, type MMChannel } from '../../services/mattermostService';
import { useEffect } from 'react';

export function MattermostSearchPage() {
  const [q, setQ] = useState('');
  const [from, setFrom] = useState('');
  const [teamId, setTeamId] = useState('');
  const [channelId, setChannelId] = useState('');
  const [after, setAfter] = useState('');
  const [before, setBefore] = useState('');
  const [teams, setTeams] = useState<MMTeam[]>([]);
  const [channels, setChannels] = useState<MMChannel[]>([]);
  const [results, setResults] = useState<MMMessage[]>([]);
  const [terms, setTerms] = useState('');
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);

  useEffect(() => {
    void (async () => {
      try {
        const [t, c] = await Promise.all([mattermostService.teams(), mattermostService.channels()]);
        setTeams(t);
        setChannels(c);
      } catch {
        /* status page covers config errors */
      }
    })();
  }, []);

  const run = async (p = 0) => {
    if (!q.trim() && !from.trim()) {
      toast.error('عبارت جستجو یا نام کاربری لازم است');
      return;
    }
    setLoading(true);
    setPage(p);
    try {
      const afterMS = after ? new Date(after).getTime() : undefined;
      const beforeMS = before ? new Date(before).getTime() : undefined;
      const res = await mattermostService.search({
        q: q.trim() || '*',
        from: from.trim() || undefined,
        teamId: teamId || undefined,
        channelId: channelId || undefined,
        after: afterMS && afterMS > 0 ? String(afterMS) : undefined,
        before: beforeMS && beforeMS > 0 ? String(beforeMS) : undefined,
        page: p,
        perPage: 40,
      });
      setResults(res.data ?? []);
      setTerms(res.terms || '');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'خطای جستجو');
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4" dir="rtl">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">جستجوی Mattermost</h1>
        <p className="mt-1 text-sm text-slate-500">جستجوی سمت‌سرور؛ کل تاریخچه دانلود نمی‌شود.</p>
      </div>

      <div className="grid gap-3 rounded-xl bg-white p-4 shadow-sm sm:grid-cols-2 lg:grid-cols-3">
        <label className="text-sm sm:col-span-2 lg:col-span-3">
          متن
          <input
            className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && void run(0)}
          />
        </label>
        <label className="text-sm">
          از کاربر
          <input
            className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            placeholder="username"
          />
        </label>
        <label className="text-sm">
          تیم
          <select
            className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2"
            value={teamId}
            onChange={(e) => setTeamId(e.target.value)}
          >
            <option value="">پیش‌فرض</option>
            {teams.map((t) => (
              <option key={t.id} value={t.id}>
                {t.display_name || t.displayName || t.name}
              </option>
            ))}
          </select>
        </label>
        <label className="text-sm">
          کانال
          <select
            className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2"
            value={channelId}
            onChange={(e) => setChannelId(e.target.value)}
          >
            <option value="">همه</option>
            {channels.map((c) => (
              <option key={c.id} value={c.id}>
                {c.displayName || c.name}
              </option>
            ))}
          </select>
        </label>
        <label className="text-sm">
          از تاریخ
          <input
            type="date"
            className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2"
            value={after}
            onChange={(e) => setAfter(e.target.value)}
          />
        </label>
        <label className="text-sm">
          تا تاریخ
          <input
            type="date"
            className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2"
            value={before}
            onChange={(e) => setBefore(e.target.value)}
          />
        </label>
        <div className="flex items-end">
          <button
            type="button"
            disabled={loading}
            onClick={() => void run(0)}
            className="inline-flex items-center gap-2 rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
          >
            <Search className="h-4 w-4" />
            جستجو
          </button>
        </div>
      </div>

      {terms ? <p className="text-xs text-slate-500">عبارت ارسالی به Mattermost: {terms}</p> : null}

      <div className="rounded-xl bg-white shadow-sm">
        {loading ? (
          <p className="p-8 text-center text-slate-500">در حال جستجو…</p>
        ) : results.length === 0 ? (
          <p className="p-8 text-center text-slate-500">نتیجه‌ای نیست.</p>
        ) : (
          <ul className="divide-y divide-slate-100">
            {results.map((m) => (
              <li key={m.id} className="px-4 py-3">
                <div className="text-xs text-slate-500">
                  #{m.channelDisplayName || m.channelName || m.channelId} · {m.displayName} (@{m.username}) ·{' '}
                  {m.createAt ? new Date(m.createAt).toLocaleString('fa-IR') : '—'}
                </div>
                <p className="mt-1 whitespace-pre-wrap text-sm text-slate-900">{m.message}</p>
                {m.permalink ? (
                  <a href={m.permalink} target="_blank" rel="noreferrer" className="text-xs text-teal-700 hover:underline">
                    لینک اصلی
                  </a>
                ) : null}
              </li>
            ))}
          </ul>
        )}
        {results.length > 0 ? (
          <div className="flex justify-between border-t px-4 py-3">
            <button
              type="button"
              disabled={page <= 0 || loading}
              className="rounded border px-3 py-1 text-sm disabled:opacity-40"
              onClick={() => void run(page - 1)}
            >
              قبلی
            </button>
            <button
              type="button"
              disabled={loading || results.length < 40}
              className="rounded border px-3 py-1 text-sm disabled:opacity-40"
              onClick={() => void run(page + 1)}
            >
              بعدی
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
}
